import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface SermonSeries {
  id: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  image_url?: string | null;
  devotional_series_id?: string | null;
  created_at?: string;
  sermon_count?: number;
}

// Fetch list of series (with aggregated sermon count)
export const useSermonSeries = () => {
  return useQuery<SermonSeries[]>({
    queryKey: ['sermonSeries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sermon_series')
        .select('id, title, subtitle, description, image_url, devotional_series_id, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      const transformed = (data ?? []).map((row: any) => ({
        id: row.id,
        title: row.title,
        subtitle: row.subtitle,
        description: row.description,
        image_url: row.image_url,
        devotional_series_id: row.devotional_series_id,
        created_at: row.created_at,
        sermon_count: row.sermons?.[0]?.count ?? 0,
      }));
      return transformed;
    },
    staleTime: 5 * 60_000,
  });
};

// Create / Update helpers
type UpsertPayload = Omit<SermonSeries, 'id' | 'created_at' | 'sermon_count'> & {
  id?: string; // optional for insert
};

export const useUpsertSeries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpsertPayload) => {
      const { id, ...rest } = payload;
      if (id) {
        // update
        const { data, error } = await supabase
          .from('sermon_series')
          .update(rest)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
      // insert
      const { data, error } = await supabase.from('sermon_series').insert(rest).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sermonSeries'] });
    },
  });
};
